import React from "react";
import { Link } from "react-router-dom";
import { Nav } from "@/components/layout/Nav";
import { Footer } from "@/components/layout/Footer";
import { PageSEO } from "@/components/seo/PageSEO";
import { SITE_URL } from "@/utils/seo";
import { ArrowRight, User, CheckCircle } from "lucide-react";

const SITE = SITE_URL;

const singles = [
  { slug: "baby-jogger-city-mini-gt2-single", name: "Baby Jogger City Mini GT2 Single", price: 22, highlight: "One-hand fold, all-terrain, up to 65 lbs. Our most popular single." },
  { slug: "bob-single-stroller", name: "BOB Single Stroller", price: 24, highlight: "Off-road performance, swivel front wheel, up to 75 lbs." },
  { slug: "uppababy-vista-v2", name: "UPPAbaby VISTA V2", price: 26, highlight: "Full-recline seat, large canopy, premium ride quality." },
  { slug: "uppababy-cruz-v2", name: "UPPAbaby Cruz V2", price: 24, highlight: "Compact footprint, easy fold, ideal for tighter spaces." },
  { slug: "nuna-mixx", name: "Nuna MIXX Next", price: 26, highlight: "Reversible seat, full recline, all-terrain wheels." },
  { slug: "nuna-trvl", name: "Nuna TRVL", price: 22, highlight: "Compact travel stroller with full-recline seat." },
  { slug: "zoe-v2-single", name: "Zoe V2 Single", price: 20, highlight: "Lightweight (14 lbs), great for travel days." },
];

const faqs = [
  {
    q: "What single stroller is best for Disney World?",
    a: "The Baby Jogger City Mini GT2 Single is our top choice for Disney World. It handles the varied terrain around the parks, folds quickly with one hand and supports children up to 65 lbs. The UPPAbaby VISTA V2 is popular for families who want a full recline for napping.",
  },
  {
    q: "Are single strollers easier to fold on Disney buses?",
    a: "Yes. Single strollers are generally lighter and fold more compactly than doubles, which makes boarding Disney buses, the Skyliner and the Monorail smoother. All of our single strollers fold quickly.",
  },
  {
    q: "What is Disney World's stroller size requirement?",
    a: "Walt Disney World requires strollers to be no larger than 31 inches wide and 52 inches long. Every single stroller in our inventory meets this requirement.",
  },
  {
    q: "Can I use a single stroller for an older child?",
    a: "Yes. Most of our single strollers support children up to 50–75 lbs depending on the model. If you have an older child who may need a break from walking, check the individual product page for exact weight limits.",
  },
  {
    q: "How light are your single strollers?",
    a: "Our lightest single is the Zoe V2 Single at approximately 14 lbs. The Baby Jogger City Mini GT2 weighs around 23 lbs. Each product page lists the exact stroller weight.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: "Strollers", item: `${SITE}/strollers` },
    { "@type": "ListItem", position: 3, name: "Single Stroller Rentals Orlando", item: `${SITE}/single-stroller-rentals-orlando` },
  ],
};

export default function SingleStrollerRentals() {
  return (
    <>
      <PageSEO
        title="Single Stroller Rentals Orlando & Disney World | Enchanted Gear Rentals"
        description="Rent a lightweight single stroller for your Orlando or Disney World trip. Baby Jogger, BOB, UPPAbaby, Nuna and Zoe — free delivery from $20/day."
        canonical={`${SITE}/single-stroller-rentals-orlando`}
        structuredData={[breadcrumbSchema, faqSchema]}
      />
      <Nav />
      <main className="min-h-screen" style={{ background: "#040A0F" }}>
        <section className="pt-28 pb-16 text-center" style={{ background: "linear-gradient(180deg, #060f18 0%, #040A0F 100%)" }}>
          <div className="container max-w-3xl">
            <nav aria-label="Breadcrumb" className="text-xs text-[#B9C8D5] mb-4">
              <Link to="/" className="hover:text-[#FFD166]">Home</Link>
              <span className="mx-2 opacity-40">/</span>
              <Link to="/strollers" className="hover:text-[#FFD166]">Strollers</Link>
              <span className="mx-2 opacity-40">/</span>
              <span className="text-[#FFD166]">Single Stroller Rentals</span>
            </nav>
            <div className="flex justify-center mb-4"><User className="w-10 h-10 text-[#FFD166]" /></div>
            <h1 className="font-serif text-4xl md:text-5xl text-[#F6F1E4] mb-5">Single Stroller Rentals Near Disney World</h1>
            <p className="text-[#B9C8D5] text-lg max-w-2xl mx-auto">
              Top-rated single strollers from Baby Jogger, BOB, Nuna, UPPAbaby and Zoe — delivered free to your Walt Disney World resort or Orlando-area hotel. All models fold easily for Disney transportation.
            </p>
            <div className="mt-7 flex gap-4 justify-center flex-wrap">
              <Link to="/#trip-setup"><button className="btn-primary" data-testid="single-cta-top">Check Availability <ArrowRight className="inline w-4 h-4 ml-1" /></button></Link>
              <Link to="/double-stroller-rentals-orlando"><button className="btn-ghost">Need a double stroller?</button></Link>
            </div>
          </div>
        </section>

        {/* What to look for */}
        <section className="container py-12">
          <h2 className="font-serif text-3xl text-[#F6F1E4] mb-6">What to look for in a Disney World stroller</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { title: "Meets Disney size rules", body: "Max 31\" wide, 52\" long. All our strollers qualify." },
              { title: "Easy to fold", body: "Quick fold speeds up boarding on Disney buses and Skyliner." },
              { title: "Full recline", body: "Lets young children nap during downtime between attractions." },
              { title: "All-terrain wheels", body: "Handles the varied surfaces around Disney's parks." },
            ].map(({ title, body }) => (
              <div key={title} className="rounded-xl bg-[#0A1825] border border-[#FFD166]/15 p-5">
                <CheckCircle className="w-5 h-5 text-[#FFD166] mb-2" />
                <div className="font-semibold text-[#F6F1E4] mb-1">{title}</div>
                <div className="text-sm text-[#B9C8D5]">{body}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Products */}
        <section className="container py-10">
          <h2 className="font-serif text-3xl text-[#F6F1E4] mb-8">Our single stroller lineup</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {singles.map((s) => (
              <Link key={s.slug} to={`/strollers/${s.slug}`} className="group rounded-xl bg-[#0A1825] border border-[#FFD166]/10 hover:border-[#FFD166]/40 p-5 transition-all hover:-translate-y-1">
                <div className="text-[10px] uppercase tracking-widest text-[#B9C8D5] border border-[#FFD166]/20 px-2 py-0.5 rounded-full w-fit mb-3">Single</div>
                <div className="font-serif text-lg text-[#F6F1E4] group-hover:text-[#FFD166] transition-colors mb-1">{s.name}</div>
                <div className="text-sm text-[#B9C8D5] mb-3">{s.highlight}</div>
                <div className="text-[#FFD166] font-semibold">From ${s.price}/day</div>
                <div className="text-xs text-[#B9C8D5] mt-1 flex items-center gap-1">Full specs &amp; photos <ArrowRight className="w-3 h-3" /></div>
              </Link>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="container py-10">
          <h2 className="font-serif text-3xl text-[#F6F1E4] mb-8">Single stroller FAQ</h2>
          <div className="space-y-4 max-w-3xl">
            {faqs.map((f) => (
              <details key={f.q} className="group rounded-xl bg-[#0A1825] border border-[#FFD166]/10 p-5">
                <summary className="font-semibold text-[#F6F1E4] cursor-pointer list-none flex justify-between items-center gap-3">
                  {f.q}
                  <span className="text-[#FFD166] text-xl leading-none shrink-0 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-[#B9C8D5] text-sm leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="container pb-20 text-center">
          <div className="max-w-xl mx-auto rounded-2xl bg-[#0A1825] border border-[#FFD166]/20 p-8">
            <h2 className="font-serif text-2xl text-[#F6F1E4] mb-3">Reserve your single stroller</h2>
            <p className="text-[#B9C8D5] mb-6">Select your dates and we will confirm availability across all single stroller models.</p>
            <Link to="/#trip-setup"><button className="btn-primary" data-testid="single-cta-bottom">Check Availability <ArrowRight className="inline w-4 h-4 ml-1" /></button></Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
